import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

function prefsDefaults(p) {
  const x = p || {};
  return {
    ticketAlerts: x.ticketAlerts !== false,
    ticketCommentAlerts: x.ticketCommentAlerts !== false,
    appLivenessAlerts: x.appLivenessAlerts !== false,
    linkLivenessAlerts: x.linkLivenessAlerts !== false,
    appLivenessAlertOnRecovery: x.appLivenessAlertOnRecovery === true,
    linkLivenessAlertOnRecovery: x.linkLivenessAlertOnRecovery === true,
  };
}

/**
 * Company-wide and per-monitor toggles for automated liveness alerts (Cloud Functions).
 * Shown to company_admin / developer on the Alerts settings flow.
 */
export default function AlertLivenessSourcesSettings({ userData, compact = false }) {
  const companyId = userData?.companyId;
  const role = userData?.role;
  const allowed = role === 'company_admin' || role === 'developer';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prefs, setPrefs] = useState(() => prefsDefaults({}));
  const [apps, setApps] = useState([]);
  const [links, setLinks] = useState([]);
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(async () => {
    if (!companyId || !allowed) {
      setApps([]);
      setLinks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [coSnap, appsSnap, linksSnap] = await Promise.all([
        getDoc(doc(db, 'companies', companyId)),
        getDocs(query(collection(db, 'companyApps'), where('companyId', '==', companyId))),
        getDocs(query(collection(db, 'links'), where('companyId', '==', companyId))),
      ]);
      const raw = coSnap.exists() ? (coSnap.data().notificationPrefs || {}) : {};
      setPrefs(prefsDefaults(raw));
      const appRows = appsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      appRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setApps(appRows);
      const linkRows = linksSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      linkRows.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      setLinks(linkRows);
    } catch (e) {
      console.error('AlertLivenessSourcesSettings:', e);
      setError(e.message || 'Could not load notification settings.');
    } finally {
      setLoading(false);
    }
  }, [companyId, allowed]);

  useEffect(() => {
    load();
  }, [load]);

  const patchCompanyPrefs = async (partial) => {
    if (!companyId) return;
    setSavingKey('company');
    setError('');
    try {
      const next = { ...prefs, ...partial };
      setPrefs(next);
      const ref = doc(db, 'companies', companyId);
      const payload = {};
      Object.keys(partial).forEach((k) => {
        payload[`notificationPrefs.${k}`] = partial[k];
      });
      await updateDoc(ref, payload);
    } catch (e) {
      console.error('patchCompanyPrefs:', e);
      setError(e.message || 'Could not save company settings.');
      await load();
    } finally {
      setSavingKey('');
    }
  };

  const patchApp = async (appId, livenessAlertsEnabled) => {
    setSavingKey(`app:${appId}`);
    setError('');
    try {
      await updateDoc(doc(db, 'companyApps', appId), { livenessAlertsEnabled });
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, livenessAlertsEnabled } : a)));
    } catch (e) {
      console.error('patchApp:', e);
      setError(e.message || 'Could not update app.');
      await load();
    } finally {
      setSavingKey('');
    }
  };

  const patchLink = async (linkId, livenessAlertsEnabled) => {
    setSavingKey(`link:${linkId}`);
    setError('');
    try {
      await updateDoc(doc(db, 'links', linkId), { livenessAlertsEnabled });
      setLinks((prev) => prev.map((l) => (l.id === linkId ? { ...l, livenessAlertsEnabled } : l)));
    } catch (e) {
      console.error('patchLink:', e);
      setError(e.message || 'Could not update link.');
      await load();
    } finally {
      setSavingKey('');
    }
  };

  if (!allowed || !companyId) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Ticket notifications
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Ticket creates, updates, archive/restore, and escalations can appear in the same notifications inbox as health notices.
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.ticketAlerts}
              disabled={Boolean(savingKey)}
              onChange={(_, v) => patchCompanyPrefs({ ticketAlerts: v })}
            />
          )}
          label="Ticket activity in notifications inbox"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.ticketCommentAlerts}
              disabled={Boolean(savingKey) || !prefs.ticketAlerts}
              onChange={(_, v) => patchCompanyPrefs({ ticketCommentAlerts: v })}
            />
          )}
          label="Include new comments on tickets"
        />
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Automated health notifications
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Controls what the scheduled monitors are allowed to post to your inbox. Manual checks from Uptime Monitoring
        or External Links still run; they only skip creating a notification when disabled here.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack spacing={0.5}>
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.appLivenessAlerts}
              disabled={Boolean(savingKey)}
              onChange={(_, v) => patchCompanyPrefs({ appLivenessAlerts: v })}
            />
          )}
          label="App / site monitors — send liveness notifications"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.appLivenessAlertOnRecovery}
              disabled={Boolean(savingKey) || !prefs.appLivenessAlerts}
              onChange={(_, v) => patchCompanyPrefs({ appLivenessAlertOnRecovery: v })}
            />
          )}
          label="Also notify when a monitor returns to LIVE (recovery)"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.linkLivenessAlerts}
              disabled={Boolean(savingKey)}
              onChange={(_, v) => patchCompanyPrefs({ linkLivenessAlerts: v })}
            />
          )}
          label="External links — send liveness notifications"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={prefs.linkLivenessAlertOnRecovery}
              disabled={Boolean(savingKey) || !prefs.linkLivenessAlerts}
              onChange={(_, v) => patchCompanyPrefs({ linkLivenessAlertOnRecovery: v })}
            />
          )}
          label="Also notify when a link returns to LIVE (recovery)"
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Per app / site
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Turn off noisy monitors (for example staging URLs) without disabling the rest of the company.
      </Typography>
      {apps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No app monitors yet.</Typography>
      ) : (
        <Stack spacing={0.25}>
          {apps.map((app) => (
            <FormControlLabel
              key={app.id}
              control={(
                <Switch
                  size="small"
                  checked={app.livenessAlertsEnabled !== false}
                  disabled={savingKey === `app:${app.id}`}
                  onChange={(_, v) => patchApp(app.id, v)}
                />
              )}
              label={app.name || app.homepageUrl || app.id}
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Per external link
      </Typography>
      {links.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No links for this company.</Typography>
      ) : (
        <Stack spacing={0.25}>
          {links.map((link) => (
            <FormControlLabel
              key={link.id}
              control={(
                <Switch
                  size="small"
                  checked={link.livenessAlertsEnabled !== false}
                  disabled={savingKey === `link:${link.id}`}
                  onChange={(_, v) => patchLink(link.id, v)}
                />
              )}
              label={link.title || link.url || link.id}
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
